import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from '../types'

const DEFAULT_DECIMAL_PLACES = 1

type TDewPointUnit = 'c' | 'f'

type TCanopy = 'ABOVE' | 'IN' | 'BELOW'

const dewPointFunctionMaker =
  (canopy: TCanopy) =>
  (
    dewPointTemperature: number | null = null,
    {
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'c',
      outputUnit = userDetailsStore.getState().temperature === 'IMPERIAL' ? 'f' : 'c',
    }: TUnitConverterOptionsWithUnits<TDewPointUnit> = {},
  ) => {
    let convertedValue: number | null

    const shouldConvertFromCToF = inputUnit === 'c' && outputUnit === 'f'
    const shouldConvertFromFToC = inputUnit === 'f' && outputUnit === 'c'

    if (isNil(dewPointTemperature)) {
      convertedValue = null
    } else if (shouldConvertFromCToF) {
      convertedValue = dewPointTemperature * 1.8 + 32
    } else if (shouldConvertFromFToC) {
      convertedValue = (dewPointTemperature - 32) / 1.8
    } else {
      // assume there is no conversion required
      convertedValue = Number(dewPointTemperature)
    }

    const unitSymbol =
      outputUnit === 'c'
        ? translate.measurements.degreesCelsius.unit()
        : translate.measurements.degreesFahrenheit.unit()

    return {
      categoryTitle: () =>
        translate.phrases.templates('{{label}} ({{unitSymbol}})', {
          label: translate.phrases.banyanApp('Dew Point'),
          unitSymbol,
        }),
      categoryTitleWithoutUnit: () => translate.phrases.banyanApp('Dew Point'),
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => {
        if (canopy === 'ABOVE') {
          return translate.phrases.banyanApp('Above Canopy')
        }

        if (canopy === 'IN') {
          return translate.phrases.banyanApp('In Canopy')
        }

        return translate.phrases.banyanApp('Below Canopy')
      },
      title: () => {
        if (canopy === 'ABOVE') {
          return translate.phrases.templates('{{labelA}} ({{labelB}}) {{unitSymbol}}', {
            labelA: translate.phrases.banyanApp('Dew Point'),
            labelB: translate.phrases.banyanApp('Above Canopy'),
            unitSymbol,
          })
        }

        if (canopy === 'IN') {
          return translate.phrases.templates('{{labelA}} ({{labelB}}) {{unitSymbol}}', {
            labelA: translate.phrases.banyanApp('Dew Point'),
            labelB: translate.phrases.banyanApp('In Canopy'),
            unitSymbol,
          })
        }

        return translate.phrases.templates('{{labelA}} ({{labelB}}) {{unitSymbol}}', {
          labelA: translate.phrases.banyanApp('Dew Point'),
          labelB: translate.phrases.banyanApp('Below Canopy'),
          unitSymbol,
        })
      },
      titleWithoutUnit: () => {
        if (canopy === 'ABOVE') {
          return translate.phrases.templates('{{labelA}} ({{labelB}})', {
            labelA: translate.phrases.banyanApp('Dew Point'),
            labelB: translate.phrases.banyanApp('Above Canopy'),
          })
        }

        if (canopy === 'IN') {
          return translate.phrases.templates('{{labelA}} ({{labelB}})', {
            labelA: translate.phrases.banyanApp('Dew Point'),
            labelB: translate.phrases.banyanApp('In Canopy'),
          })
        }

        return translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Dew Point'),
          labelB: translate.phrases.banyanApp('Below Canopy'),
        })
      },
      value: () => {
        if (isNil(convertedValue)) return null

        return +convertedValue?.toFixed(decimalPlaces)
      },
      valueAsString: () => {
        if (outputUnit === 'c')
          return translate.measurements.degreesCelsius.value(convertedValue, decimalPlaces)

        return translate.measurements.degreesFahrenheit.value(convertedValue, decimalPlaces)
      },
      valueWithSuffix: () => {
        if (outputUnit === 'c')
          return translate.measurements.degreesCelsius.valueWithUnit(convertedValue, decimalPlaces)

        return translate.measurements.degreesFahrenheit.valueWithUnit(convertedValue, decimalPlaces)
      },
      valueWithNoRounding: () => convertedValue,
    }
  }

type TDewPointOptions = 'dewPointAboveCanopy' | 'dewPointInCanopy' | 'dewPointBelowCanopy'

export const dewPoint: Record<
  TDewPointOptions,
  (
    dewPointTemperature?: number | null,
    options?: TUnitConverterOptionsWithUnits<TDewPointUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  dewPointAboveCanopy: dewPointFunctionMaker('ABOVE'),
  dewPointInCanopy: dewPointFunctionMaker('IN'),
  dewPointBelowCanopy: dewPointFunctionMaker('BELOW'),
}
